import { useState } from 'react';
import { Alert, Button, Input, Modal, Result, Space, Spin } from 'antd';
import { useExtractTopic } from '@/hooks/useExtractTopic';
import { ReviewForm } from './ReviewForm';
import type { ExtractResponse } from '@/types/extractedTopic';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  investorOptions: string[];
}

type Phase = 'edit' | 'extracting' | 'review' | 'failed';

export function PasteTextFlow({ open, onClose, onSaved, investorOptions }: Props) {
  const [phase, setPhase] = useState<Phase>('edit');
  const [text, setText] = useState('');
  const [result, setResult] = useState<ExtractResponse | null>(null);
  const [errMsg, setErrMsg] = useState<string | undefined>();
  const extractMut = useExtractTopic();

  function reset() {
    setPhase('edit');
    setText('');
    setResult(null);
    setErrMsg(undefined);
  }

  async function run() {
    setPhase('extracting');
    try {
      const res = await extractMut.mutateAsync({ source: 'text', text });
      if (res.status === 'failed') {
        setErrMsg(res.message ?? res.error ?? 'extraction failed');
        setPhase('failed');
      } else {
        setResult(res);
        setPhase('review');
      }
    } catch (e) {
      setErrMsg((e as Error).message);
      setPhase('failed');
    }
  }

  return (
    <Modal
      open={open}
      title="Paste flyer content"
      width={phase === 'review' ? 1200 : 700}
      onCancel={() => {
        reset();
        onClose();
      }}
      footer={null}
      destroyOnHidden
    >
      {phase === 'edit' && (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Input.TextArea
            rows={14}
            placeholder={
              'Loan Type: 7/6 ARM\nLTV: 75% to $3M (CA) / 70% to $2M (other)\nMinimum FICO: 680\n…'
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <Space>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Cancel
            </Button>
            <Button type="primary" disabled={text.trim().length === 0} onClick={run}>
              Extract
            </Button>
          </Space>
        </Space>
      )}

      {phase === 'extracting' && <Result icon={<Spin size="large" />} title="Extracting…" />}

      {phase === 'failed' && (
        <>
          <Alert type="error" message="Extraction failed" description={errMsg} showIcon />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={reset}>Try again</Button>
            <Button
              onClick={() => {
                reset();
                onClose();
              }}
            >
              Close
            </Button>
          </Space>
        </>
      )}

      {phase === 'review' && result?.draft && (
        <ReviewForm
          draft={result.draft}
          confidence={result.confidence ?? 0}
          warnings={result.warnings ?? []}
          rawResponse={result.raw_response}
          investorOptions={investorOptions}
          onSaved={() => {
            reset();
            onSaved();
            onClose();
          }}
          onDiscard={reset}
        />
      )}
    </Modal>
  );
}
